import { useCallback, useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getHomeserverFromClient } from 'utils/matrixUtils';

export type SlugAvailabilityStatus = 'IDLE' | 'CHECKING' | 'AVAILABLE' | 'UNAVAILABLE';

const DEBOUNCE_MS = 500;

const M_NOT_FOUND = 'M_NOT_FOUND';

interface UseSlugAvailabilityOptions {
  /**
   * Initial status returned without calling the backend. When set, the debounced check is
   * skipped while `hasSlugChanged === false` — used by VideoHubInlineEditForm to seed
   * AVAILABLE for the room's existing alias (which is by definition free for itself).
   */
  initialStatus?: SlugAvailabilityStatus;
  /**
   * When false (and `initialStatus` is set), the hook returns `initialStatus` and skips the
   * debounced backend check. Once the user edits the slug (`hasSlugChanged=true`), the hook
   * resumes its normal behaviour.
   */
  hasSlugChanged?: boolean;
  /**
   * The slug the room already owns. When the current `slug` matches this value the room
   * already owns this alias, so the backend check is skipped and AVAILABLE is returned.
   * Used by VideoHubInlineEditForm to handle the "re-enter original slug" case.
   */
  originalSlug?: string;
}

const useSlugAvailability = (
  slug: string,
  postfix: string,
  options: UseSlugAvailabilityOptions = {}
): SlugAvailabilityStatus => {
  const { initialStatus, hasSlugChanged = true, originalSlug } = options;
  const { matrixClient } = useMatrixContext();
  const [status, setStatus] = useState<SlugAvailabilityStatus>(initialStatus ?? 'IDLE');

  const checkAvailability = useCallback(
    async (currentSlug: string) => {
      setStatus('CHECKING');
      try {
        const alias = `#${currentSlug}-${postfix}:${getHomeserverFromClient(matrixClient)}`;
        const resp = await matrixClient.getRoomIdForAlias(alias);
        if (resp?.room_id) {
          setStatus('UNAVAILABLE');
        } else {
          setStatus('AVAILABLE');
        }
      } catch (error) {
        const errcode = (error as { errcode?: string })?.errcode;
        if (errcode === M_NOT_FOUND) {
          setStatus('AVAILABLE');
        } else {
          setStatus('UNAVAILABLE');
        }
      }
    },
    [matrixClient, postfix]
  );

  useEffect(() => {
    // Inline-edit seed path: while the user hasn't touched the slug, the room owns its alias
    // already → skip the backend check entirely and report the seeded status.
    if (initialStatus !== undefined && !hasSlugChanged) {
      setStatus(initialStatus);
      return;
    }

    // If the user has edited back to the original slug the room already owns that alias.
    if (originalSlug !== undefined && slug === originalSlug) {
      setStatus('AVAILABLE');
      return;
    }

    if (!slug) {
      setStatus('IDLE');
      return;
    }

    const timer = setTimeout(() => {
      checkAvailability(slug);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [slug, checkAvailability, initialStatus, hasSlugChanged, originalSlug]);

  return status;
};

export default useSlugAvailability;
